import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Search, Filter, RefreshCw, X, Loader2, Settings2, EyeOff } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger
} from '@/components/ui/popover';

/**
 * FilterBar - Shared filter bar component for dashboards
 * 
 * Features:
 * - Search input with icon
 * - Status filter dropdown
 * - Sort indicator with clear button
 * - Column settings button
 * - Save layout button
 * - Expandable filter section
 * - Custom action buttons
 */

// Search Input Component
export const FilterSearch = ({
  value,
  onChange,
  placeholder = 'Search...',
  testId = 'search-input'
}) => (
  <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
    <Input
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="pl-10 bg-slate-950 border-slate-800 text-white w-full"
      data-testid={testId}
    />
  </div>
);

// Status Filter Dropdown
export const FilterSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'All Items',
  allLabel = 'All',
  icon: Icon = Filter,
  className = 'w-[180px]',
  testId
}) => (
  <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
    <SelectTrigger className={`bg-slate-950 border-slate-700 text-white ${className}`} data-testid={testId}>
      {Icon && <Icon className="h-4 w-4 mr-2" />}
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="bg-slate-900 border-slate-700">
      <SelectItem value="all" className="text-slate-300">{allLabel}</SelectItem>
      {options.map((opt) => {
        const optValue = typeof opt === 'string' ? opt : opt.id || opt.value;
        const optLabel = typeof opt === 'string' ? opt : opt.name || opt.label || optValue;
        return (
          <SelectItem key={optValue} value={optValue} className="text-slate-300">
            {optLabel}
          </SelectItem>
        );
      })}
    </SelectContent>
  </Select>
);

// Filter Toggle Button
export const FilterToggleButton = ({
  showFilters,
  onClick,
  hasActiveFilters,
  label = 'Filter'
}) => (
  <Button
    variant="outline"
    size="sm"
    onClick={onClick}
    className={`border-slate-700 ${hasActiveFilters ? 'bg-blue-600/20 border-blue-500' : ''}`}
    data-testid="filter-toggle-btn"
  >
    <Filter className="h-4 w-4 mr-2" />
    {label}
    {hasActiveFilters && <Badge className="ml-2 bg-blue-600 h-5 px-1.5">!</Badge>}
  </Button>
);

// Clear Sort Button
export const ClearSortButton = ({
  sortColumn,
  sortDirection,
  onClear
}) => {
  if (!sortColumn) return null;
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClear}
      className="border-blue-500 text-blue-400 hover:bg-blue-900/30"
      data-testid="clear-sort-btn"
    >
      <X className="h-4 w-4 mr-1" />
      Sort: {sortColumn} {sortDirection === 'asc' ? '↑' : '↓'}
    </Button>
  );
};

// Column Settings Button
export const ColumnSettingsButton = ({ onClick, label = 'Columns' }) => (
  <Button
    variant="outline"
    size="sm"
    onClick={onClick}
    className="border-slate-700 text-slate-300"
    data-testid="column-settings-btn"
  >
    <Settings2 className="h-4 w-4 mr-1" />
    {label}
  </Button>
);

// Save Layout Button
export const SaveLayoutButton = ({
  columnsChanged,
  saving,
  onSave
}) => {
  if (!columnsChanged) return null;
  return (
    <Button
      size="sm"
      onClick={onSave}
      disabled={saving}
      className="bg-green-600 hover:bg-green-500"
      data-testid="save-columns-btn"
    >
      {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
      Save Layout
    </Button>
  );
};

// Refresh Button
export const RefreshButton = ({ onClick, loading = false }) => (
  <Button
    variant="outline"
    size="sm"
    onClick={onClick}
    className="border-slate-700"
    data-testid="refresh-btn"
  >
    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
  </Button>
);

// Include Closed Checkbox
export const IncludeClosedCheckbox = ({
  checked,
  onChange,
  label = 'Include Closed'
}) => (
  <div className="flex items-center gap-2">
    <Checkbox
      id="includeClosed"
      checked={checked}
      onCheckedChange={onChange}
      className="border-slate-600"
      data-testid="include-closed-checkbox"
    />
    <label htmlFor="includeClosed" className="text-sm text-slate-400 cursor-pointer">
      {label}
    </label>
  </div>
);

// Clear Filters Button
export const ClearFiltersButton = ({ 
  hasActiveFilters, 
  onClick,
  label = 'Clear Filters'
}) => {
  if (!hasActiveFilters) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className="text-slate-400 hover:text-white"
      data-testid="clear-filters-btn"
    >
      <X className="h-4 w-4 mr-1" />
      {label}
    </Button>
  );
};

/**
 * Main FilterBar Component
 * Combines all filter elements into a cohesive bar
 */
export function FilterBar({
  // Search
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  searchTestId = 'search-input',
  // Status filter
  statusValue,
  onStatusChange,
  statusOptions = [],
  statusPlaceholder = 'All Statuses',
  // Additional filters (rendered in expanded section)
  additionalFilters,
  showFilters = false,
  onToggleFilters,
  hasActiveFilters = false,
  onClearFilters,
  // Sort
  sortColumn,
  sortDirection,
  onClearSort,
  // Column settings
  showColumnSettings = false,
  onOpenColumnSettings,
  columnsChanged = false,
  savingColumns = false,
  onSaveColumns,
  // Refresh
  showRefresh = false,
  onRefresh,
  refreshLoading = false,
  // Include closed
  showIncludeClosed = false,
  includeClosed = false,
  onIncludeClosedChange,
  // Hidden statuses
  hiddenStatuses = [],
  onHiddenStatusesChange,
  // Custom actions (rendered at end of main row)
  customActions,
  // Styling
  className = ''
}) {
  return (
    <Card className={`bg-slate-900 border-slate-800 p-3 sm:p-4 ${className}`}>
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
        {/* Search */}
        <FilterSearch
          value={searchValue}
          onChange={onSearchChange}
          placeholder={searchPlaceholder}
          testId={searchTestId}
        />
        
        <div className="flex items-center gap-2 flex-wrap">
          {/* Status Filter (inline) - only if no expandable filters */}
          {!additionalFilters && statusOptions.length > 0 && (
            <FilterSelect
              value={statusValue}
              onChange={onStatusChange}
              options={statusOptions}
              placeholder={statusPlaceholder}
              allLabel="All Statuses"
            />
          )}
          
          {/* Filter Toggle (if has expandable filters) */}
          {additionalFilters && (
            <FilterToggleButton
              showFilters={showFilters}
              onClick={onToggleFilters}
              hasActiveFilters={hasActiveFilters}
            />
          )}
          
          {/* Include Closed */}
          {showIncludeClosed && (
            <IncludeClosedCheckbox
              checked={includeClosed}
              onChange={onIncludeClosedChange}
            />
          )}
          
          {/* Hide Statuses */}
          {onHiddenStatusesChange && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className={`text-xs gap-1 ${hiddenStatuses.length > 0 ? 'border-orange-500/50 text-orange-400' : 'border-slate-700 text-slate-400'}`} data-testid="hide-statuses-btn">
                  <EyeOff className="h-3 w-3" />
                  Hide Status{hiddenStatuses.length > 0 ? ` (${hiddenStatuses.length})` : ''}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="bg-slate-900 border-slate-700 w-56 p-3" align="start">
                <div className="space-y-2">
                  <p className="text-xs text-slate-400 font-medium mb-2">Hide these statuses:</p>
                  {['New Shipment', 'Available for Pickup', 'On the way to Pick Up', 
                    'At the Port', 'Picked Up', 'On the way to Deliver', 
                    'Delivered', 'Delivered - Waiting for Unload', 'Empty Ready to Return',
                    'Empty Container in Yard', 'On the way to Return',
                    'Container Returned', 'Shipment Closed'].map(st => (
                    <label key={st} className="flex items-center gap-2 cursor-pointer text-xs text-slate-300 hover:text-white">
                      <Checkbox
                        checked={hiddenStatuses.includes(st)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            onHiddenStatusesChange([...hiddenStatuses, st]);
                          } else {
                            onHiddenStatusesChange(hiddenStatuses.filter(s => s !== st));
                          }
                        }}
                        className="h-3.5 w-3.5"
                      />
                      {st}
                    </label>
                  ))}
                  {hiddenStatuses.length > 0 && (
                    <Button variant="ghost" size="sm" className="text-xs text-slate-400 w-full mt-1" onClick={() => onHiddenStatusesChange([])}>
                      Clear all
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}
          
          {/* Refresh */}
          {showRefresh && (
            <RefreshButton onClick={onRefresh} loading={refreshLoading} />
          )}
          
          {/* Clear Sort */}
          <ClearSortButton
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onClear={onClearSort}
          />
          
          {/* Column Settings */}
          {showColumnSettings && (
            <ColumnSettingsButton onClick={onOpenColumnSettings} />
          )}
          
          {/* Save Layout */}
          <SaveLayoutButton
            columnsChanged={columnsChanged}
            saving={savingColumns}
            onSave={onSaveColumns}
          />
          
          {/* Custom Actions */}
          {customActions}
        </div>
      </div>
      
      {/* Expandable Filters Row */}
      {showFilters && additionalFilters && (
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-4 pt-4 border-t border-slate-800">
          {additionalFilters}
          
          {/* Clear Filters Button */}
          <ClearFiltersButton
            hasActiveFilters={hasActiveFilters}
            onClick={onClearFilters}
          />
        </div>
      )}
    </Card>
  );
}

export default FilterBar;
